<template>
  <div>
    <!-- Scenario 1: Empty State -->
    <RewardCard
      v-if="!hasReward && !showCustomRewardSelector"
      :isClickable="true"
      :isSelected="false"
      @click="handleOuterCardClick"
    >
      <RewardEmptyState :paymentLanguage="paymentLanguage" :paymentIcon="paymentIcon" />
    </RewardCard>

    <!-- Scenario 2: Custom Input Mode -->
    <RewardCard v-else-if="showCustomRewardSelector" :isClickable="false" :isSelected="true">
      <RewardInputState
        :customTitle="customInputTitle"
        :paymentLanguage="paymentLanguage"
        :paymentIcon="paymentIcon"
        :showBudgetSelector="showBudgetSelector"
        :budgets="budgets"
        :loadingBudgets="loadingBudgets"
        :budgetId="budgetId"
        :customEmoji="customEmoji"
        :customAmount="customAmount"
        :cashAward="cashAward"
        @update-budgetId="updateBudgetId"
        @update-custom-amount="updateCustomAmount"
        @update-custom-emoji="updateCustomEmoji"
        @update-valid-amount="updateValidAmount"
        @blur="onBlur"
      />
    </RewardCard>

    <!-- Scenario 3 & 4: Selected Reward Display (with or without budget) -->
    <RewardCard v-else-if="hasReward" :isClickable="false" :isSelected="true">
      <RewardDisplayState
        :paymentLanguage="paymentLanguage"
        :paymentIcon="paymentIcon"
        :rewardPresetName="rewardPresetName"
        :emoji="emoji"
        :amount="amount"
        :paymentPresetId="paymentPresetId"
        :cashAward="cashAward"
        :showBudgetSelector="showBudgetSelector"
        :showFundBudgetButton="showFundBudgetButton"
        :budgets="budgets"
        :loadingBudgets="loadingBudgets"
        :budgetId="budgetId"
        :showStarAnimation="lottie.star"
        @edit="handleEditClick"
        @clear="clearReward"
        @update-budgetId="updateBudgetId"
        @blur="onBlur"
        @fund-budget="handleFundBudget"
      />
    </RewardCard>

    <!-- Drawer for selecting rewards -->
    <v-navigation-drawer v-model="drawer" temporary fixed right width="500">
      <RewardDrawer
        v-if="drawer"
        :loading="loading"
        :cashAward="cashAward"
        :presets="presets"
        :customEmoji="customEmoji"
        :customAmount="customAmount"
        :budgetId="budgetId"
        :budgets="budgets"
        :loadingBudgets="loadingBudgets"
        :showBudgetSelector="showBudgetSelectorInDrawer"
        @close="drawer = false"
        @select-reward="selectReward"
        @select-custom-reward="selectCustomReward"
        @update-custom-amount="updateCustomAmount"
        @update-custom-emoji="updateCustomEmoji"
        @update-budgetId="updateBudgetId"
        @blur="onBlur"
      />
    </v-navigation-drawer>
    <v-overlay v-if="drawer" />
  </div>
</template>

<script>
import RewardCard from "@/components/recognition/RewardSelector/RewardCard.vue";
import RewardEmptyState from "@/components/recognition/RewardSelector/RewardEmptyState.vue";
import RewardInputState from "@/components/recognition/RewardSelector/RewardInputState.vue";
import RewardDisplayState from "@/components/recognition/RewardSelector/RewardDisplayState.vue";
import RewardDrawer from "@/components/recognition/RewardSelector/RewardDrawer.vue";

export default {
  name: "RewardSelector",
  components: {
    RewardCard,
    RewardEmptyState,
    RewardInputState,
    RewardDisplayState,
    RewardDrawer,
  },
  props: {
    customInputTitle: {
      type: String,
      default: null,
    },
    paymentLanguage: {
      type: String,
      default: "Reward",
    },
    paymentIcon: {
      type: String,
      default: "mdi-gift-outline",
    },
    reviewPage: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    cashAward: {
      type: Boolean,
      default: true,
    },
    presets: {
      type: Array,
      default: () => [],
    },
    emoji: {
      type: String,
      default: null,
    },
    amount: {
      type: Number,
      default: null,
    },
    paymentPresetId: {
      type: Number,
      default: null,
    },
    customEmoji: {
      type: String,
      default: null,
    },
    customAmount: {
      type: String,
      default: null,
    },
    rewardPresetName: {
      type: String,
      default: null,
    },
    showBudgetSelector: {
      type: Boolean,
      default: false,
    },
    showFundBudgetButton: {
      type: Boolean,
      default: false,
    },
    showBudgetSelectorInDrawer: {
      type: Boolean,
      default: false,
    },
    showCustomRewardSelector: {
      type: Boolean,
      default: false,
    },
    emitEditEvent: {
      type: Boolean,
      default: false,
    },
    budgets: {
      type: Array,
      default: () => [],
    },
    loadingBudgets: {
      type: Boolean,
      default: false,
    },
    budgetId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      drawer: false,
      lottie: {
        star: false,
      },
    };
  },
  methods: {
    onBlur() {
      this.$emit("blur");
    },
    selectReward(e) {
      this.playStarAnimation();
      this.$emit("select-reward", e);
      this.drawer = false;
    },
    selectCustomReward() {
      this.playStarAnimation();
      this.$emit("select-custom-reward");
      this.drawer = false;
    },
    updateCustomAmount(e) {
      this.$emit("update-custom-amount", e);
    },
    updateCustomEmoji(e) {
      this.$emit("update-custom-emoji", e);
    },
    handleFundBudget() {
      this.$emit("fund-budget");
    },
    updateBudgetId(e) {
      this.$emit("update-budgetId", e);
    },
    updateValidAmount(e) {
      this.$emit("update-valid-amount", e);
    },
    clearReward() {
      this.$emit("clear");
    },
    playStarAnimation() {
      this.lottie.star = true;
      setTimeout(() => {
        this.lottie.star = false;
      }, 1000);
    },
    handleOuterCardClick() {
      this.drawer = true;
    },
    handleEditClick() {
      if (this.emitEditEvent) {
        this.$emit("edit-custom-reward");
        return;
      }
      this.drawer = true;
    },
  },
  computed: {
    hasReward() {
      return !!(this.emoji && this.amount);
    },
  },
};
</script>

<!-- 
  RewardSelector now composes state-specific components:
  - RewardCard: Base card wrapper with border states
  - RewardEmptyState: Empty "Add a reward" state
  - RewardInputState: Custom input mode with budget + amount input
  - RewardDisplayState: Selected reward display with edit/delete actions
  
  This makes each scenario clear and maintainable.
-->
